// Orders inlezen (docs/tms-orderintake.md §3g): the rows of a list in a customer's mail, made into the orders WLX makes
// of them. Which column fills which field is the mail channel's (by the header); the rest comes from the list as a whole
// — the loading and unloading day when the rows do not say them (IDP puts them in the subject), the week a day of the
// week falls in (the Lattiz planning says "dinsdag 01:00"), the reference prefix, the loading metres per pallet and per
// carrier. Pure, so it is tested on its own; the API looks the places up and makes the orders.

export interface ListCell {
  text: string;
  date?: string;
  time?: string;
}

export interface MailList {
  key: string;
  kind: "attachment" | "body";
  label: string;
  header: string[];
  rows: ListCell[][];
}

/** The fields a column can fill (the API's LIST_FIELDS). */
export const LIST_FIELDS = [
  "reference",
  "name",
  "loadDate",
  "loadTime",
  "loadReference",
  "loadInstruction",
  "loadName",
  "loadStreet",
  "loadPostalCode",
  "loadCity",
  "loadCountry",
  "unloadDate",
  "unloadTime",
  "unloadReference",
  "unloadInstruction",
  "unloadName",
  "unloadStreet",
  "unloadPostalCode",
  "unloadCity",
  "unloadCountry",
  "pallets",
  "carrier",
  "loadingMeters",
  "weightKg",
  "description",
  "remark",
] as const;
export type ListField = (typeof LIST_FIELDS)[number];
export type FieldMap = Partial<Record<ListField, string>>;

// ─── Values ──────────────────────────────────────────────────────────────────

const pad = (value: number) => String(value).padStart(2, "0");

export function addDays(date: string, days: number): string {
  const day = new Date(`${date}T12:00:00Z`);
  day.setUTCDate(day.getUTCDate() + days);
  return day.toISOString().slice(0, 10);
}

/** The Monday of the week this day is in. */
export function mondayOf(date: string): string {
  const day = new Date(`${date}T12:00:00Z`);
  const weekday = day.getUTCDay() === 0 ? 7 : day.getUTCDay();
  return addDays(date, 1 - weekday);
}

/** The ISO week of a day: 2 November 2026 is week 45. */
export function isoWeek(date: string): number {
  const day = new Date(`${date}T12:00:00Z`);
  const weekday = day.getUTCDay() === 0 ? 7 : day.getUTCDay();
  day.setUTCDate(day.getUTCDate() + 4 - weekday);
  const yearStart = new Date(Date.UTC(day.getUTCFullYear(), 0, 1));
  return Math.ceil(((day.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/** The working day before this one: Monday 26-10 → Friday 23-10. */
export function previousWorkingDay(date: string): string {
  let day = addDays(date, -1);
  while ([0, 6].includes(new Date(`${day}T12:00:00Z`).getUTCDay())) day = addDays(day, -1);
  return day;
}

/** A channel's prefix for this week: {week} is the ISO week ("Lattiz wk{week}" → "Lattiz wk45"). */
export function prefixFor(pattern: string | null, day: string): string {
  return (pattern ?? "").replace(/\{week\}/gi, String(isoWeek(day)));
}

/**
 * A number as lists write it: "9410", "9.410" (a thousand), "9410,5", "13.2". Null for nothing or no number. A point
 * with three digits after it and no comma is a thousand separator, as Dutch lists write them.
 */
export function parseNumber(text: string): number | null {
  const cleaned = text.trim().replace(/\s/g, "");
  if (!cleaned) return null;
  let normal = cleaned;
  if (cleaned.includes(",")) normal = cleaned.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) normal = cleaned.replace(/\./g, "");
  const value = Number(normal);
  return Number.isFinite(value) ? value : null;
}

/** Loading metres as shown: at most two decimals, a comma. */
export function metresText(metres: number | null): string {
  if (metres === null || !Number.isFinite(metres)) return "";
  return String(Math.round(metres * 100) / 100).replace(".", ",");
}

// The day of the week by its first two letters, in Dutch and English: "dindsdag" and "woensag" are still days.
const WEEKDAYS: Record<string, number> = { ma: 1, di: 2, wo: 3, do: 4, vr: 5, za: 6, zo: 7, mo: 1, tu: 2, we: 3, th: 4, fr: 5, sa: 6, su: 7 };

/**
 * When a cell says it is: an Excel date and time, "26-10-2026", "26-10" (in the year of the list), "2026-10-26",
 * "dinsdag 01:00" (in the week of the list), "08:00" or "07:00-15:00". Each part is undefined where the cell does not
 * say it.
 */
export function whenOf(cell: ListCell | undefined, context: { year: number; week: string }): { date?: string; from?: string; to?: string } {
  if (!cell) return {};
  if (cell.date) return { date: cell.date, ...(cell.time ? { from: cell.time } : {}) };
  const text = cell.text.trim();
  if (!text) return {};
  const time = (h: string, m: string) => `${pad(Number(h))}:${m}`;
  const range = /(\d{1,2})[:.](\d{2})\s*[-–]\s*(\d{1,2})[:.](\d{2})/.exec(text);
  const single = /(?:^|\s)(\d{1,2})[:.](\d{2})(?:\s*u(?:ur)?)?(?:\s|$)/.exec(text);
  const times = range ? { from: time(range[1]!, range[2]!), to: time(range[3]!, range[4]!) } : single && Number(single[1]) < 24 ? { from: time(single[1]!, single[2]!) } : {};
  const iso = /(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
  if (iso) return { date: `${iso[1]}-${pad(Number(iso[2]))}-${pad(Number(iso[3]))}`, ...times };
  const dmy = /(?:^|\D)(\d{1,2})[-./](\d{1,2})(?:[-./](\d{2,4}))?(?:\D|$)/.exec(text);
  if (dmy && !range && !(single && dmy.index === single.index)) {
    const year = dmy[3] ? Number(dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3]) : context.year;
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) return { date: `${year}-${pad(month)}-${pad(day)}`, ...times };
  }
  const weekday = /^([a-z]{2})[a-z]*\.?(?:\s|$)/i.exec(text);
  const number = weekday ? WEEKDAYS[weekday[1]!.toLowerCase()] : undefined;
  if (number) return { date: addDays(mondayOf(context.week), number - 1), ...times };
  return times;
}

// ─── Guessing the columns ────────────────────────────────────────────────────

const GUESSES: [ListField, RegExp][] = [
  ["reference", /^(bon|order ?nr|ordernummer|referentie|reference|zending)\b/i],
  ["unloadDate", /lever ?datum|leverdatum|losdatum|delivery date/i],
  ["loadDate", /laad ?datum|loading date/i],
  ["unloadReference", /losreferentie|delivery ref/i],
  ["loadReference", /laadreferentie|loading ref/i],
  ["unloadName", /^(naam|name|ontvanger|consignee|losnaam)$/i],
  ["unloadStreet", /^(straat|street|adres|address|losadres)$/i],
  ["unloadPostalCode", /^(postcode|postal code|zip|lospostcode)$/i],
  ["unloadCity", /^(plaats|gemeente|city|woonplaats|losplaats)$/i],
  ["unloadCountry", /^(land|country|losland)$/i],
  ["unloadInstruction", /commentaar adres|lossen opmerking|delivery comment/i],
  ["pallets", /^(aantal|pallets|aantal pallets|quantity)\b/i],
  ["carrier", /drager ?type|carrier|palletsoort|eenheid/i],
  ["weightKg", /bruto|gewicht|weight|kg/i],
  ["loadingMeters", /laadmeter|ldm/i],
];

/** The columns a header names plainly, for a channel that has none yet: the person checks and changes them. */
export function guessFields(header: string[]): FieldMap {
  const fields: FieldMap = {};
  for (const [field, pattern] of GUESSES) {
    const found = header.find((name) => pattern.test(name.trim()) && !Object.values(fields).includes(name));
    if (found) fields[field] = found;
  }
  return fields;
}

// ─── The rows ────────────────────────────────────────────────────────────────

export interface RowPlace {
  name?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
}

/** A place of the address book, as a place rule names it. */
export interface PlaceRef {
  id: string;
  name: string;
  city: string | null;
}

/**
 * The place of a stop by a text in a column (Camiel, 26-10-2026: the Lattiz loads go to Haps and to Weerts): the first
 * text the row's cell holds gives its place; a row whose cell holds none takes the standard order's.
 */
export interface PlaceRuleView {
  header: string;
  /** A place that is gone is null: that text gives none. */
  places: { contains: string; location: PlaceRef | null }[];
}
export type PlaceRulesView = Partial<Record<"load" | "unload", PlaceRuleView>>;

interface RowSide {
  date: string;
  from?: string;
  to?: string;
  reference?: string | null;
  instruction?: string | null;
  place?: RowPlace | null;
  /** The place a rule chose by a text in the row. */
  locationId?: string;
}

/** A row as the API takes it (POST /order-templates/:id/orders). */
export interface ListRowInput {
  customerReference: string;
  load: RowSide;
  unload: RowSide;
  pallets: number | null;
  loadingMeters: number;
  weightKg?: number | null;
  description?: string | null;
  remark?: string | null;
}

export type RowProblem = "reference" | "loadDate" | "unloadDate" | "loadingMeters" | "unloadBeforeLoad";

/** A row of the list as the screen shows it: what it becomes, why it cannot, or that the channel leaves it out. */
export interface ListRow {
  index: number;
  input: ListRowInput;
  problems: RowProblem[];
  /** Left out by the channel's row filter. */
  filtered: boolean;
  /** The place a rule chose per side by a text in the row; null: none did. */
  ruled: { load: PlaceRef | null; unload: PlaceRef | null };
}

export interface ListSettings {
  fields: FieldMap;
  /** The days of the list as a whole: when the rows do not say them. */
  loadDate: string | null;
  unloadDate: string | null;
  /** A day of the week the week a weekday falls in: any day of it. */
  week: string;
  prefix: string;
  metresPerPallet: number | null;
  /** Per carrier (lower case): IDP's euro is 0,4 m. */
  metresByCarrier: Record<string, number>;
  loadDateRule: "previous_working_day" | null;
  rowFilter: { header: string; contains: string } | null;
  placeRules?: PlaceRulesView;
}

/** What the list makes of each row. */
export function rowsOfList(list: MailList, settings: ListSettings): ListRow[] {
  const column = (field: ListField) => {
    const header = settings.fields[field];
    if (!header) return -1;
    return list.header.findIndex((name) => name.trim().toLowerCase() === header.trim().toLowerCase());
  };
  const columns = Object.fromEntries(LIST_FIELDS.map((field) => [field, column(field)])) as Record<ListField, number>;
  const filterColumn = settings.rowFilter ? list.header.findIndex((name) => name.trim().toLowerCase() === settings.rowFilter!.header.trim().toLowerCase()) : -1;
  const context = { year: Number((settings.loadDate ?? settings.unloadDate ?? settings.week).slice(0, 4)), week: settings.week };

  return list.rows.map((cells, index) => {
    const cell = (field: ListField) => (columns[field] >= 0 ? cells[columns[field]] : undefined);
    const text = (field: ListField) => cell(field)?.text.trim() || null;
    // A rule's place goes before the address columns.
    const ruledOf = (side: "load" | "unload"): PlaceRef | null => {
      const rule = settings.placeRules?.[side];
      if (!rule) return null;
      const at = list.header.findIndex((name) => name.trim().toLowerCase() === rule.header.trim().toLowerCase());
      const value = at >= 0 ? (cells[at]?.text ?? "").toLowerCase() : "";
      return (value && rule.places.find((one) => one.location && value.includes(one.contains.trim().toLowerCase()))?.location) || null;
    };
    const ruled = { load: ruledOf("load"), unload: ruledOf("unload") };
    const place = (side: "load" | "unload"): RowPlace | null => {
      if (ruled[side]) return null;
      const name = text(`${side}Name`);
      const street = text(`${side}Street`);
      const postalCode = text(`${side}PostalCode`);
      const city = text(`${side}City`);
      const countryCode = text(`${side}Country`);
      return name || street || postalCode || city ? { name, street, postalCode, city, countryCode } : null;
    };

    // When: the row's own columns, then the list's days; the loading day by the channel's rule.
    const loadWhen = { ...whenOf(cell("loadDate"), context), ...stripDate(whenOf(cell("loadTime"), context), cell("loadDate")) };
    const unloadWhen = { ...whenOf(cell("unloadDate"), context), ...stripDate(whenOf(cell("unloadTime"), context), cell("unloadDate")) };
    const unloadDate = unloadWhen.date ?? settings.unloadDate ?? null;
    const loadDate =
      loadWhen.date ?? settings.loadDate ?? (settings.loadDateRule === "previous_working_day" && unloadDate ? previousWorkingDay(unloadDate) : null);
    // Without an unloading day the load unloads the day it loads, as the Lattiz loads to Haps do.
    const unloadDay = unloadDate ?? loadDate;
    // A time on its own is that time: "dinsdag 01:00" loads at 01:00 (Camiel, 24-10-2026).
    const window = (when: { from?: string; to?: string }) => (when.from ? { from: when.from, to: when.to ?? when.from } : {});
    // No time to unload: none is made up (Camiel, 25-10-2026: "Waarom zouden we deze niet leeglaten? er zijn immers toch
    // geen opgegeven lostijden, alleen openingstijden"). The API gives it the unloading place's hours, as any order
    // typed without a time; a load after closing time then waits in Aandacht nodig (lossen vóór laden): either the
    // hours or the order is not right.
    const unloadWindow = window(unloadWhen);

    const pallets = text("pallets") !== null ? parseNumber(text("pallets")!) : null;
    const carrier = text("carrier")?.toLowerCase() ?? null;
    const perPallet = (carrier ? settings.metresByCarrier[carrier] : undefined) ?? settings.metresPerPallet;
    const metres = text("loadingMeters") !== null ? parseNumber(text("loadingMeters")!) : pallets !== null && perPallet !== null ? Math.round(pallets * perPallet * 100) / 100 : null;
    const weight = text("weightKg") !== null ? parseNumber(text("weightKg")!) : null;
    const own = text("reference") ?? text("name");
    const reference = [settings.prefix.trim(), own].filter(Boolean).join(" ");

    const input: ListRowInput = {
      customerReference: reference,
      load: {
        date: loadDate ?? "",
        ...window(loadWhen),
        ...(columns.loadReference >= 0 ? { reference: text("loadReference") } : {}),
        ...(columns.loadInstruction >= 0 ? { instruction: text("loadInstruction") } : {}),
        ...(place("load") ? { place: place("load") } : {}),
        ...(ruled.load ? { locationId: ruled.load.id } : {}),
      },
      unload: {
        date: unloadDay ?? "",
        ...unloadWindow,
        ...(columns.unloadReference >= 0 ? { reference: text("unloadReference") } : {}),
        ...(columns.unloadInstruction >= 0 ? { instruction: text("unloadInstruction") } : {}),
        ...(place("unload") ? { place: place("unload") } : {}),
        ...(ruled.unload ? { locationId: ruled.unload.id } : {}),
      },
      pallets: pallets !== null ? Math.round(pallets) : null,
      loadingMeters: metres ?? 0,
      ...(weight !== null ? { weightKg: weight } : {}),
      ...(columns.description >= 0 ? { description: text("description") } : {}),
      ...(columns.remark >= 0 ? { remark: text("remark") } : {}),
    };

    const problems: RowProblem[] = [];
    if (!own) problems.push("reference");
    if (!loadDate) problems.push("loadDate");
    if (!unloadDay) problems.push("unloadDate");
    if (!metres || metres <= 0 || metres > 30) problems.push("loadingMeters");
    if (loadDate && unloadDay && unloadDay < loadDate) problems.push("unloadBeforeLoad");
    const filtered =
      settings.rowFilter !== null &&
      !(filterColumn >= 0 && (cells[filterColumn]?.text ?? "").toLowerCase().includes(settings.rowFilter.contains.trim().toLowerCase()));
    return { index, input, problems, filtered, ruled };
  });
}

/** A time column read on its own: its day only counts when there is no day column. */
function stripDate(when: { date?: string; from?: string; to?: string }, dayCell: ListCell | undefined): { date?: string; from?: string; to?: string } {
  if (dayCell && (dayCell.date || dayCell.text.trim())) {
    const { date: _date, ...rest } = when;
    return rest;
  }
  return when;
}

/**
 * The list of a mail an ordermail reads: the one whose header holds most of its columns, the first on a tie. A forward
 * can put a piece of the table above the original without its header (Anne, 26-10-2026: "onderstaande nog invoeren"),
 * and the first table is then not the one the columns were set on.
 */
export function bestList<T extends { header: string[] }>(lists: T[], fields: FieldMap): T | null {
  const wanted = [...new Set(Object.values(fields).filter((header): header is string => Boolean(header)).map((header) => header.trim().toLowerCase()))];
  let best: T | null = lists[0] ?? null;
  let most = 0;
  for (const list of lists) {
    const names = new Set(list.header.map((name) => name.trim().toLowerCase()));
    const found = wanted.filter((header) => names.has(header)).length;
    if (found > most) {
      most = found;
      best = list;
    }
  }
  return best;
}

/** Whether a list has a day of the week in a mapped date or time column: then the week of the list matters. */
export function usesWeekdays(list: MailList, fields: FieldMap): boolean {
  const columns = (["loadDate", "loadTime", "unloadDate", "unloadTime"] as const)
    .map((field) => fields[field])
    .filter((header): header is string => Boolean(header))
    .map((header) => list.header.findIndex((name) => name.trim().toLowerCase() === header.trim().toLowerCase()))
    .filter((column) => column >= 0);
  return list.rows.some((row) => columns.some((column) => /^[a-z]{2,}\.?\s/i.test(row[column]?.text.trim() ?? "") && !row[column]?.date));
}
